/**
 * CartBundleGrouping
 * -------------------
 * Purely visual/organizational layer over the minicart drawer's line items.
 *
 * Every line item rendered by snippets/cart-item.liquid carries:
 *   data-cart-item
 *   data-line-key="{{ item.key }}"
 *   data-bundle-id="{{ item.properties._bundle_id }}"
 *   data-bundle-product="{{ item.properties._bundle_product }}"
 *   data-line-price="{{ item.final_line_price | money_without_currency | remove: ',' }}"
 *
 * When 2+ line items share the same non-empty data-bundle-id, this script
 * wraps them in a single ".bundle-group" card and injects a combined
 * subtotal line beneath them. Items with no bundle id (blank, e.g. a normal
 * single-variant add) are left completely untouched in place.
 *
 * This NEVER changes cart.total_price, item quantities, or line items
 * themselves — it only rearranges/wraps existing DOM nodes and adds a
 * read-only subtotal display. Shopify's real total (rendered elsewhere in
 * minicart.liquid via {{ cart.total_price }}) is unaffected.
 *
 * ENTRY POINTS
 * ------------
 * - window.CartBundleGrouping.run(container) — called by
 *   variant-add-list.js immediately after it swaps in fresh #minicart-form
 *   HTML following an add-to-cart request.
 * - DOMContentLoaded — runs once on initial page load so bundles already
 *   in the cart (e.g. page refresh) are grouped without needing an add.
 *
 * NOTE: the drawer's quantity +/- and remove buttons are currently handled
 * by markup/JS that does not call CartBundleGrouping.run() after updating.
 * If/when that flow is wired (separate from this bundle feature), it should
 * also call CartBundleGrouping.run() after it swaps in fresh line-item HTML,
 * or grouping will go stale until the next add-to-cart / page load.
 */
(function () {
  const CONTAINER_SELECTOR = '#minicart-form .items';
  const ITEM_SELECTOR = '[data-cart-item]';
  const GROUP_CLASS = 'bundle-group';
  const GROUP_HEADER_CLASS = 'bundle-group__header';
  const GROUP_SUBTOTAL_CLASS = 'bundle-group__subtotal';

  function formatMoney(cents) {
    // Uses Shopify.formatMoney if the theme has it (most Shopify themes
    // load it globally); falls back to a plain decimal string otherwise.
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.moneyFormat) {
      try {
        return window.Shopify.formatMoney(cents, window.moneyFormat);
      } catch (e) {
        /* fall through */
      }
    }
    return (cents / 100).toFixed(2);
  }

  function parseLinePrice(el) {
    const raw = el.getAttribute('data-line-price');
    const value = parseFloat(raw);
    if (Number.isNaN(value)) return 0;
    // data-line-price is emitted via money_without_currency, i.e. a
    // decimal major-unit string like "33.99" — convert to integer cents
    // for safe summation (avoids floating point drift).
    return Math.round(value * 100);
  }

  function groupItemsByBundle(items) {
    const groups = new Map(); // bundleId -> [elements]
    const ungrouped = [];

    items.forEach((el) => {
      const bundleId = (el.getAttribute('data-bundle-id') || '').trim();
      if (!bundleId) {
        ungrouped.push(el);
        return;
      }
      if (!groups.has(bundleId)) groups.set(bundleId, []);
      groups.get(bundleId).push(el);
    });

    return { groups, ungrouped };
  }

  function buildGroupWrapper(bundleId, productLabel) {
    const wrapper = document.createElement('div');
    wrapper.className = GROUP_CLASS;
    wrapper.setAttribute('data-bundle-group', bundleId);

    const header = document.createElement('div');
    header.className = GROUP_HEADER_CLASS;
    header.textContent = productLabel || 'Bundle';
    wrapper.appendChild(header);

    return wrapper;
  }

  function buildSubtotalRow(totalCents) {
    const row = document.createElement('div');
    row.className = GROUP_SUBTOTAL_CLASS;

    const label = document.createElement('span');
    label.className = GROUP_SUBTOTAL_CLASS + '-label';
    label.textContent = 'Bundle subtotal';

    const value = document.createElement('span');
    value.className = GROUP_SUBTOTAL_CLASS + '-value';
    value.textContent = formatMoney(totalCents);

    row.appendChild(label);
    row.appendChild(value);
    return row;
  }

  function clearExistingGroups(container) {
    // If this runs twice on the same markup (e.g. run() called more than
    // once without a fresh swap), unwrap any previous grouping first so we
    // don't nest groups inside groups.
    container.querySelectorAll('[data-bundle-group]').forEach((group) => {
      const parent = group.parentNode;
      Array.from(group.querySelectorAll(ITEM_SELECTOR)).forEach((item) => {
        parent.insertBefore(item, group);
      });
      group.remove();
    });
  }

  function run(scope) {
    const root = scope || document;
    const container = root.matches && root.matches(CONTAINER_SELECTOR)
      ? root
      : root.querySelector
        ? root.querySelector(CONTAINER_SELECTOR)
        : null;

    if (!container) return;

    clearExistingGroups(container);

    const items = Array.from(container.querySelectorAll(ITEM_SELECTOR));
    if (items.length === 0) return;

    const { groups } = groupItemsByBundle(items);
    if (groups.size === 0) return; // nothing to group, leave DOM as-is

    groups.forEach((groupItems, bundleId) => {
      if (groupItems.length < 2) return; // a "group" of 1 isn't worth a card

      const firstItem = groupItems[0];
      const productLabel = firstItem.getAttribute('data-bundle-product') || '';
      const wrapper = buildGroupWrapper(bundleId, productLabel);

      // Insert the wrapper right before the first item in this bundle,
      // then move all of that bundle's items inside it, in their existing
      // DOM order.
      firstItem.parentNode.insertBefore(wrapper, firstItem);
      let totalCents = 0;
      groupItems.forEach((el) => {
        wrapper.appendChild(el);
        totalCents += parseLinePrice(el);
      });

      wrapper.appendChild(buildSubtotalRow(totalCents));
    });
  }

  window.CartBundleGrouping = { run };

  document.addEventListener('DOMContentLoaded', function () {
    run(document);
  });
})();